<script setup>
// Funil padrão do Kanban por inbox (`inboxes.kanban_pipeline_id`).
//
// A regra que a tela precisa deixar explícita, porque foi a confusão que
// originou a feature: a chave do card é (contato, funil) enquanto ele estiver
// aberto. Recontato NÃO abre card novo — a conversa nova pendura no card que já
// existe. Quem espera "1 card por conversa" configura errado e só descobre no
// board.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { toast } from 'dashboard/helper/toast';
import { pipelinesAPI } from 'dashboard/api/kanban';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Multiselect from 'vue-multiselect';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();
const { accountScopedRoute } = useAccount();

// Estado local (não o módulo Vuex `kanban`): despachar `kanban/fetchPipelines`
// daqui selecionaria um funil e buscaria o board inteiro só pra preencher um
// select. Erro de busca NÃO pode virar lista vazia — vazio leria como "esta
// conta não tem funil", e o admin criaria um funil que já existe.
const pipelines = ref([]);
const isLoading = ref(false);
const loadError = ref(null);

const isSaving = ref(false);
// Opção do Multiselect ({ value, label } ou null).
const selectedOption = ref(null);
// true = mostrar o seletor mesmo já havendo funil vinculado (modo "trocar").
const isReplacing = ref(false);

const fetchPipelines = async () => {
  isLoading.value = true;
  loadError.value = null;
  try {
    // `GET /pipelines` responde um ARRAY puro (index.json.jbuilder faz
    // `json.array!`), não um envelope `{ payload }`. Shape diferente não vira
    // lista vazia: denuncia.
    const { data } = await pipelinesAPI.get();
    if (!Array.isArray(data)) {
      throw new Error('resposta do GET pipelines não é uma lista');
    }
    pipelines.value = data;
  } catch (error) {
    loadError.value =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      String(error);
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchPipelines();
  // Re-busca a inbox pra não decidir em cima de um kanban_pipeline_id em cache.
  store.dispatch('inboxes/get');
});

// Só funis vivos são oferecidos — vincular a um arquivado é escolher algo que
// não aparece no board.
const selectablePipelines = computed(() =>
  pipelines.value.filter(p => !p.archived)
);

const linkedPipelineId = computed(() => props.inbox.kanban_pipeline_id || null);

// Procura na lista INTEIRA (inclusive arquivados): se o funil vinculado sumiu
// ou foi arquivado, a tela tem que dizer isso, não fingir "não configurado".
const linkedPipeline = computed(
  () => pipelines.value.find(p => p.id === linkedPipelineId.value) || null
);

const isLinkedMissing = computed(
  () =>
    Boolean(linkedPipelineId.value) &&
    !linkedPipeline.value &&
    !isLoading.value &&
    !loadError.value
);

const pipelineOptions = computed(() =>
  selectablePipelines.value.map(p => ({
    value: p.id,
    label: p.name,
    kind: p.kind,
  }))
);

// A inbox também roda IA: o fluxo pode registrar o lead em OUTRO funil pelas
// ações do Studio. Divulgado de propósito — dois cards para o mesmo contato é
// consequência esperada da combinação, não bug a esconder.
const showAiOverlapWarning = computed(() => Boolean(props.inbox.ai_project_id));

const setInboxPipeline = pipelineId =>
  store.dispatch('inboxes/updateInbox', {
    id: props.inbox.id,
    formData: false,
    kanban_pipeline_id: pipelineId,
  });

const startReplacing = () => {
  isReplacing.value = true;
  selectedOption.value = null;
};

const cancelReplacing = () => {
  isReplacing.value = false;
  selectedOption.value = null;
};

const linkPipeline = async () => {
  const pipelineId = selectedOption.value?.value;
  if (!pipelineId || isSaving.value) return;

  isSaving.value = true;
  try {
    await setInboxPipeline(pipelineId);
    selectedOption.value = null;
    isReplacing.value = false;
    toast.success(t('INBOX_MGMT.KANBAN_CONFIG.LINKED'));
  } catch (error) {
    toast.error(
      t('INBOX_MGMT.KANBAN_CONFIG.ERROR_LINK', {
        error: error?.message || String(error),
      })
    );
  } finally {
    isSaving.value = false;
  }
};

const unlinkPipeline = async () => {
  if (!linkedPipelineId.value || isSaving.value) return;

  isSaving.value = true;
  try {
    await setInboxPipeline(null);
    selectedOption.value = null;
    isReplacing.value = false;
    toast.success(t('INBOX_MGMT.KANBAN_CONFIG.UNLINKED'));
  } catch (error) {
    toast.error(
      t('INBOX_MGMT.KANBAN_CONFIG.ERROR_UNLINK', {
        error: error?.message || String(error),
      })
    );
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="mx-8">
    <LoadingState v-if="isLoading && pipelines.length === 0" />
    <div v-else class="flex flex-wrap mx-0">
      <SettingsSection
        :title="t('INBOX_MGMT.KANBAN_CONFIG.TITLE')"
        :sub-title="t('INBOX_MGMT.KANBAN_CONFIG.DESCRIPTION')"
      >
        <div class="space-y-4">
          <!-- Como a feature funciona. Fica sempre visível: é a regra que o
               admin precisa ler ANTES de vincular. -->
          <div
            class="flex flex-col gap-1.5 p-3 rounded-lg border border-solid border-n-weak bg-n-solid-2"
          >
            <p class="flex items-start gap-2 text-xs text-n-slate-11">
              <span
                class="i-lucide-git-branch-plus size-3.5 flex-shrink-0 mt-px"
              />
              {{ t('INBOX_MGMT.KANBAN_CONFIG.BEHAVIOR') }}
            </p>
            <p class="flex items-start gap-2 text-xs text-n-slate-11">
              <span class="i-lucide-user-round size-3.5 flex-shrink-0 mt-px" />
              {{ t('INBOX_MGMT.KANBAN_CONFIG.CARD_KEY') }}
            </p>
          </div>

          <!-- A inbox também roda IA: dois funis, dois cards. Divulgação. -->
          <p
            v-if="showAiOverlapWarning"
            class="flex items-start gap-2 p-3 rounded-lg text-xs bg-n-amber-3 text-n-amber-11"
          >
            <span class="i-lucide-triangle-alert size-4 flex-shrink-0 mt-px" />
            {{ t('INBOX_MGMT.KANBAN_CONFIG.AI_OVERLAP_WARNING') }}
          </p>

          <!-- Falha de busca é falha, não "conta sem funil". -->
          <p
            v-if="loadError"
            class="flex items-start gap-2 p-3 rounded-lg text-xs bg-n-ruby-3 text-n-ruby-11"
          >
            <span class="i-lucide-circle-alert size-4 flex-shrink-0 mt-px" />
            {{ t('INBOX_MGMT.KANBAN_CONFIG.LOAD_ERROR', { error: loadError }) }}
          </p>

          <template v-else>
            <!-- Vinculado a um funil que sumiu/foi arquivado. -->
            <p
              v-if="isLinkedMissing"
              class="flex items-start gap-2 p-3 rounded-lg text-xs bg-n-amber-3 text-n-amber-11"
            >
              <span
                class="i-lucide-triangle-alert size-4 flex-shrink-0 mt-px"
              />
              {{
                t('INBOX_MGMT.KANBAN_CONFIG.BROKEN_LINK', {
                  id: linkedPipelineId,
                })
              }}
            </p>

            <!-- Funil vinculado (modo "visualizar") -->
            <template v-if="linkedPipelineId && !isReplacing">
              <div
                v-if="linkedPipeline"
                class="flex items-center gap-3 p-3 rounded-lg border border-solid border-n-teal-6 bg-n-teal-2"
              >
                <span class="i-lucide-columns-3 size-5 text-n-teal-11" />
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2">
                    <p class="text-sm font-medium text-n-slate-12 truncate">
                      {{ linkedPipeline.name }}
                    </p>
                    <span
                      v-if="linkedPipeline.archived"
                      class="inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold uppercase rounded-full bg-n-amber-3 text-n-amber-11"
                    >
                      {{ t('INBOX_MGMT.KANBAN_CONFIG.ARCHIVED') }}
                    </span>
                  </div>
                  <p
                    v-if="linkedPipeline.archived"
                    class="mt-0.5 text-xs text-n-amber-11"
                  >
                    {{ t('INBOX_MGMT.KANBAN_CONFIG.ARCHIVED_HINT') }}
                  </p>
                  <p
                    v-else-if="linkedPipeline.description"
                    class="mt-0.5 text-xs text-n-slate-10 truncate"
                  >
                    {{ linkedPipeline.description }}
                  </p>
                </div>
                <router-link
                  :to="accountScopedRoute('kanban_board')"
                  class="text-xs text-n-blue-11 hover:underline whitespace-nowrap"
                >
                  {{ t('INBOX_MGMT.KANBAN_CONFIG.OPEN_KANBAN') }}
                </router-link>
              </div>

              <div class="flex gap-2">
                <NextButton
                  type="button"
                  :label="t('INBOX_MGMT.KANBAN_CONFIG.REPLACE')"
                  :disabled="isSaving || pipelineOptions.length === 0"
                  @click="startReplacing"
                />
                <NextButton
                  type="button"
                  :label="t('INBOX_MGMT.KANBAN_CONFIG.UNLINK')"
                  :disabled="isSaving"
                  :is-loading="isSaving"
                  faded
                  ruby
                  @click="unlinkPipeline"
                />
              </div>
            </template>

            <!-- Seletor (sem funil vinculado, ou trocando) -->
            <template v-else>
              <div v-if="pipelineOptions.length > 0" class="space-y-3">
                <div class="block">
                  <span class="block mb-1 text-sm font-medium text-n-slate-12">
                    {{ t('INBOX_MGMT.KANBAN_CONFIG.SELECT_LABEL') }}
                  </span>
                  <Multiselect
                    v-model="selectedOption"
                    :options="pipelineOptions"
                    track-by="value"
                    label="label"
                    :allow-empty="false"
                    :show-labels="false"
                    searchable
                    :placeholder="t('INBOX_MGMT.KANBAN_CONFIG.PLACEHOLDER')"
                  />
                </div>
                <div class="flex gap-2">
                  <NextButton
                    type="button"
                    :label="
                      linkedPipelineId
                        ? t('INBOX_MGMT.KANBAN_CONFIG.SAVE')
                        : t('INBOX_MGMT.KANBAN_CONFIG.LINK')
                    "
                    :disabled="!selectedOption || isSaving"
                    :is-loading="isSaving"
                    @click="linkPipeline"
                  />
                  <NextButton
                    v-if="linkedPipelineId"
                    type="button"
                    :label="t('INBOX_MGMT.KANBAN_CONFIG.CANCEL')"
                    faded
                    :disabled="isSaving"
                    @click="cancelReplacing"
                  />
                </div>
              </div>
              <!-- Conta sem NENHUM funil: não oferece seletor vazio. -->
              <div v-else class="text-sm text-n-slate-10">
                <p>{{ t('INBOX_MGMT.KANBAN_CONFIG.EMPTY') }}</p>
                <router-link
                  :to="accountScopedRoute('kanban_board')"
                  class="text-n-blue-11 hover:underline"
                >
                  {{ t('INBOX_MGMT.KANBAN_CONFIG.CREATE') }}
                </router-link>
              </div>
            </template>
          </template>
        </div>
      </SettingsSection>
    </div>
  </div>
</template>
